/**
 * The Application-experience board's reading rules: what a per-app tile claims,
 * which tone it wears, how the hourly series keeps its gaps, and how the folded
 * page keys and the idle probes are described.
 *
 * Like `features/dynamics/console-format.ts`, this module imports **no runtime
 * values** — only types, which the compiler erases — so `node --test` exercises
 * it directly under Node's own type stripping. Every decision that could quietly
 * turn "not measured" into "fine" is therefore covered by a test rather than by
 * a reviewer reading JSX.
 *
 * The honesty rules it holds:
 *
 * - **A null latency is a null.** `ttfb_p50_ms: null` means no request in the
 *   lookback carried a timing, so the tile shows an em dash on a neutral tone.
 *   It never becomes `0 ms`, and it never wears success.
 * - **An app with no posters is silent, not fast.** `users === 0` puts the tile
 *   in the `silent` state, whose caption says nobody reported — the opposite
 *   finding from "everyone reported and everything was quick".
 * - **`error_rate: null` is unknown; `error_rate: 0` is a measured zero.** Only
 *   the second may go green.
 * - **Coverage exists for F&O only.** The other four apps have no denominator
 *   (nothing counts "active Power BI users" in this workspace), so their tiles
 *   say the ratio does not exist rather than drawing an empty meter.
 * - **An hour with no rows is absent, not zero**, so the pivot leaves the app's
 *   value `null` and the line breaks there.
 * - **Folded page keys are stated.** A `k`-anonymity `Other` row keeps its own
 *   label, its user count stays null, and the caption counts the fold.
 *
 * The thresholds here (TTFB, interaction latency, error rate) are **this
 * board's own reading thresholds**, not backend parameters like the F&O
 * report's `FormP95MsParam`. Every caption that uses one names it, so nobody
 * mistakes a NOVA convention for a measured SLO.
 */

import type {
  FoConsoleExperienceApp,
  FoConsoleExperienceContext,
  FoConsoleExperienceHour,
  FoConsoleExperienceProbe,
  FoConsoleRumCoverage,
} from "@/lib/api";
import type { StatusTone } from "@/lib/charts/config";

/** The one "nothing to show" glyph, identical to the console's. */
export const EM_DASH = "—";

/* ------------------------------------------------------------------------ *
 * Thresholds — the board's own reading, stated wherever it is used
 * ------------------------------------------------------------------------ */

/** Median TTFB the board starts calling slow, in ms. */
export const TTFB_WARNING_MS = 800;
/** Median TTFB the board calls bad, in ms. */
export const TTFB_DANGER_MS = 2000;
/** Median interaction latency (Event Timing) the board starts calling sluggish, in ms. */
export const INTERACTION_WARNING_MS = 200;
/** Median interaction latency the board calls bad, in ms — Google's INP "poor" boundary. */
export const INTERACTION_DANGER_MS = 500;
/** Share of requests failing before the board warns, 0..1. */
export const ERROR_RATE_WARNING = 0.01;
/** Share of requests failing before the board calls it bad, 0..1. */
export const ERROR_RATE_DANGER = 0.05;
/** Idle-probe median the board calls slow, in ms — the tier-4 probe threshold. */
export const PROBE_SLOW_MS = 1000;

/** One sentence naming the thresholds above, for the board's legend. */
export const THRESHOLD_CAPTION =
  `NOVA's own reading thresholds, not a measured SLO: TTFB warns above ${TTFB_WARNING_MS} ms, ` +
  `interaction latency above ${INTERACTION_WARNING_MS} ms, error rate above ` +
  `${(ERROR_RATE_WARNING * 100).toFixed(0)} % of requests.`;

/* ------------------------------------------------------------------------ *
 * Formatters that are this board's own (durations come from console-format)
 * ------------------------------------------------------------------------ */

/** A 0..1 share as a whole percentage — `71 %`; an em dash when absent. */
export function formatShare01(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) {
    return EM_DASH;
  }
  return `${Math.round(Math.min(1, Math.max(0, value)) * 100)} %`;
}

/**
 * A 0..1 rate as a percentage with one decimal — `0.8 %`. Error rates live near
 * zero, so rounding them to whole percent would print every real problem as
 * `0 %`. An absent rate is an em dash, never `0.0 %`.
 */
export function formatRate01(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) {
    return EM_DASH;
  }
  const pct = Math.min(1, Math.max(0, value)) * 100;
  return `${pct.toFixed(1)} %`;
}

/** A count with thousands separators; an em dash when the count is unknown. */
export function formatCount(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) {
    return EM_DASH;
  }
  return value.toLocaleString();
}

/* ------------------------------------------------------------------------ *
 * Tones
 * ------------------------------------------------------------------------ */

/** Tone for a latency median against the board's thresholds; `null` is neutral, never green. */
export function latencyTone(
  value: number | null | undefined,
  warningMs: number,
  dangerMs: number,
): StatusTone {
  if (value == null || !Number.isFinite(value)) {
    return "neutral";
  }
  if (value >= dangerMs) {
    return "danger";
  }
  if (value >= warningMs) {
    return "warning";
  }
  return "success";
}

/**
 * Tone for an error rate. `null` is neutral — the requests were not counted, so
 * nothing is known — while a measured `0` is the one case that may go green.
 */
export function errorRateTone(value: number | null | undefined): StatusTone {
  if (value == null || !Number.isFinite(value)) {
    return "neutral";
  }
  if (value >= ERROR_RATE_DANGER) {
    return "danger";
  }
  if (value >= ERROR_RATE_WARNING) {
    return "warning";
  }
  return "success";
}

/* ------------------------------------------------------------------------ *
 * Per-app tiles
 * ------------------------------------------------------------------------ */

/**
 * `reporting` — browsers posted minutes for this app in the lookback.
 * `silent` — the app is enabled in the extension but nobody posted, which is a
 * rollout fact, not a performance verdict.
 */
export type ExperienceTileState = "reporting" | "silent";

export interface ExperienceCoverageView {
  /** Whether a coverage ratio exists for this app at all (F&O only today). */
  applicable: boolean;
  pct: number | null;
  with_rum: number | null;
  total: number | null;
  /** `146 of 185 (78.9 %)`, or the honest fallback for a missing denominator. */
  text: string;
  /** Why the ratio reads as it does — always rendered beside it. */
  caption: string;
}

export interface ExperienceTile {
  app: string;
  state: ExperienceTileState;
  /** Distinct browsers reporting. A count, and a lower bound on the fleet. */
  users: number;
  /** User-minutes with a row — how much evidence the medians rest on. */
  minutes: number;
  ttfb_p50_ms: number | null;
  ttfb_p95_ms: number | null;
  ttfbTone: StatusTone;
  interaction_p50_ms: number | null;
  interaction_p75_ms: number | null;
  interactionTone: StatusTone;
  error_rate: number | null;
  errorRateText: string;
  errorTone: StatusTone;
  h3_share: number | null;
  h3ShareText: string;
  coverage: ExperienceCoverageView;
  /** The line under the numbers: how much evidence, and what is missing. */
  caption: string;
}

/**
 * The coverage view for one app. `applicable` false is the four apps with no
 * denominator: the text says the ratio does not exist rather than showing a
 * 0 % meter of a source that is working fine.
 */
export function experienceCoverage(
  app: string,
  coverage: FoConsoleRumCoverage | null | undefined,
  hasDenominator: boolean,
): ExperienceCoverageView {
  if (!hasDenominator) {
    return {
      applicable: false,
      pct: null,
      with_rum: null,
      total: null,
      text: "no denominator",
      caption: `Nothing in this workspace counts the people who used ${app} without the extension, so a coverage ratio would have no denominator. The user count beside it is a lower bound.`,
    };
  }
  const total = coverage?.total ?? null;
  const withRum = coverage?.with_rum ?? null;
  const pct = coverage?.pct ?? null;
  if (total == null || total === 0 || pct == null) {
    return {
      applicable: true,
      pct: null,
      with_rum: withRum,
      total,
      text: EM_DASH,
      caption:
        "Active F&O users could not be counted in this window, so browsers-reporting over active users is unknown. Not 0 %, and not full coverage.",
    };
  }
  return {
    applicable: true,
    pct,
    with_rum: withRum,
    total,
    text: `${formatCount(withRum)} of ${formatCount(total)} (${pct.toFixed(1)} %)`,
    caption:
      "Browsers posting browser telemetry, over the people who used F&O in the same window. A person outside this share has no browser block on their records.",
  };
}

/** The tile model for one app row of the payload. */
export function experienceTile(
  row: FoConsoleExperienceApp,
  hasDenominator: boolean,
): ExperienceTile {
  const silent = row.users === 0 || row.minutes === 0;
  const coverage = experienceCoverage(row.app, row.coverage, hasDenominator);
  return {
    app: row.app,
    state: silent ? "silent" : "reporting",
    users: row.users,
    minutes: row.minutes,
    ttfb_p50_ms: row.ttfb_p50_ms,
    ttfb_p95_ms: row.ttfb_p95_ms,
    ttfbTone: latencyTone(row.ttfb_p50_ms, TTFB_WARNING_MS, TTFB_DANGER_MS),
    interaction_p50_ms: row.interaction_p50_ms,
    interaction_p75_ms: row.interaction_p75_ms,
    interactionTone: latencyTone(
      row.interaction_p50_ms,
      INTERACTION_WARNING_MS,
      INTERACTION_DANGER_MS,
    ),
    error_rate: row.error_rate,
    errorRateText: formatRate01(row.error_rate),
    errorTone: errorRateTone(row.error_rate),
    h3_share: row.h3_share,
    h3ShareText: formatShare01(row.h3_share),
    coverage,
    caption: silent
      ? "No browser posted a minute for this app in the lookback. Nothing is claimed about how it performs — this is a rollout fact, not a fast app."
      : `${formatCount(row.users)} browsers, ${formatCount(row.minutes)} user-minutes of evidence.` +
        (row.interaction_p50_ms == null
          ? " Interaction latency was not measured (extension 0.1, or nobody clicked)."
          : ""),
  };
}

/** Tiles for the payload's apps, in the order the caller sorted them. */
export function experienceTiles(
  rows: readonly FoConsoleExperienceApp[],
  hasDenominator: (app: string) => boolean,
): ExperienceTile[] {
  return rows.map((row) => experienceTile(row, hasDenominator(row.app)));
}

/* ------------------------------------------------------------------------ *
 * TTFB by hour — one row per hour, one column per app, gaps preserved
 * ------------------------------------------------------------------------ */

/** One hour of the trend: the bin, plus a TTFB median per app that reported in it. */
export interface ExperienceHourRow {
  ts: string;
  /** `null` for an app with no row in this hour — the line breaks, it does not drop to zero. */
  values: Record<string, number | null>;
}

/**
 * Pivot the hourly points to one row per hour with a value per app.
 *
 * Two things it must not do: pad a missing hour into existence (an hour nothing
 * was recorded for is absent from the payload and stays absent), and fill a
 * missing app with `0` (that would draw a silent app as the fastest one). Apps
 * absent from an hour get an explicit `null`, which `connectNulls={false}`
 * renders as a break.
 */
export function experienceHourRows(
  points: readonly FoConsoleExperienceHour[],
  apps: readonly string[],
): ExperienceHourRow[] {
  const byHour = new Map<string, Record<string, number | null>>();
  for (const point of points) {
    const row = byHour.get(point.ts) ?? Object.fromEntries(apps.map((app) => [app, null]));
    row[point.app] = point.ttfb_p50_ms;
    byHour.set(point.ts, row);
  }
  return [...byHour.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([ts, values]) => ({ ts, values }));
}

/** The apps that actually appear in the hourly series, so the legend lists no empty one. */
export function appsInHours(points: readonly FoConsoleExperienceHour[]): string[] {
  return [...new Set(points.map((point) => point.app))];
}

/* ------------------------------------------------------------------------ *
 * Worst page keys — the fold is stated, never sliced away
 * ------------------------------------------------------------------------ */

export interface ExperienceContextRow extends FoConsoleExperienceContext {
  /** What the row is called on screen — the folded row says so in words. */
  label: string;
  /** Why the user count is missing on a folded row. */
  usersNote: string | null;
  ttfbTone: StatusTone;
}

/**
 * Label and tone the worst-context rows. A folded row keeps its place in the
 * grid with an explicit label and a null user count: printing a number there
 * would leak the size of a cohort the backend folded precisely to hide it.
 */
export function experienceContextRows(
  rows: readonly FoConsoleExperienceContext[],
): ExperienceContextRow[] {
  return rows.map((row) => ({
    ...row,
    label: row.is_other ? "Other (folded)" : row.context || "no page key collected",
    usersNote: row.is_other
      ? "a folded cohort's size is not published"
      : row.users == null
        ? "not counted"
        : null,
    ttfbTone: latencyTone(row.ttfb_p95_ms, TTFB_WARNING_MS, TTFB_DANGER_MS),
  }));
}

/** How many rows the fold removed, in words — `0` still says so out loud. */
export function foldCaption(minUsers: number, folded: number): string {
  return folded === 0
    ? `No page key fell below the k≥${minUsers} floor, so nothing was folded out of this ranking.`
    : `${formatCount(folded)} page keys used by fewer than ${minUsers} people were folded into the Other row rather than dropped.`;
}

/* ------------------------------------------------------------------------ *
 * Idle probes — the path to each app host with nobody using it
 * ------------------------------------------------------------------------ */

/**
 * `failing` — probes ran and some got no answer; `slow` — answered above the
 * tier-4 threshold; `ok` — answered inside it; `no-samples` — the probe never
 * ran here, which says nothing about the host.
 */
export type ProbeVerdict = "failing" | "slow" | "ok" | "no-samples";

export interface ExperienceProbeRow extends FoConsoleExperienceProbe {
  verdict: ProbeVerdict;
  tone: StatusTone;
  /** The sentence beside the number — what the probe did and did not establish. */
  detail: string;
}

export const PROBE_VERDICT_LABEL: Record<ProbeVerdict, string> = {
  failing: "no answer",
  slow: "slow",
  ok: "inside threshold",
  "no-samples": "not probed",
};

/** Verdict, tone and words for one probe row. */
export function experienceProbeRow(row: FoConsoleExperienceProbe): ExperienceProbeRow {
  if (row.samples === 0) {
    return {
      ...row,
      verdict: "no-samples",
      tone: "neutral",
      detail:
        "No idle probe reached this host in the lookback. Nothing is known about its path — not that it is healthy.",
    };
  }
  if (row.failures > 0) {
    return {
      ...row,
      verdict: "failing",
      tone: "danger",
      detail: `${formatCount(row.failures)} of ${formatCount(row.samples)} probes got no answer. A blocked HEAD looks the same as a dead host from here, so treat it as a path finding to confirm, not a proof.`,
    };
  }
  if (row.p50_ms == null) {
    return {
      ...row,
      verdict: "no-samples",
      tone: "neutral",
      detail: `${formatCount(row.samples)} probes answered but carried no timing, so the median is unknown — not zero.`,
    };
  }
  if (row.p50_ms > PROBE_SLOW_MS) {
    return {
      ...row,
      verdict: "slow",
      tone: "warning",
      detail: `Median first byte above ${PROBE_SLOW_MS} ms with nobody using the app — the endpoint's own path, measured across ${formatCount(row.samples)} probes.`,
    };
  }
  return {
    ...row,
    verdict: "ok",
    tone: "success",
    detail: `Median first byte inside ${PROBE_SLOW_MS} ms across ${formatCount(row.samples)} probes.`,
  };
}

/** Verdicts for every probe row. */
export function experienceProbeRows(
  rows: readonly FoConsoleExperienceProbe[],
): ExperienceProbeRow[] {
  return rows.map(experienceProbeRow);
}
